/**
 * platform.js — único punto de detección de entorno de ejecución.
 *
 * Todas las referencias a window.onApp(), window.onIOSApp(), window.onAndroidApp(),
 * window.onIOSBrowser(), etc. deben resolverse aquí y consumirse vía este módulo
 * o el hook usePlatform().
 *
 * Usa getters para evaluación lazy: nativeScripts.js define las funciones en
 * window.* y es evaluado antes de cualquier render de React.
 */

/**
 * Breakpoints (px) para clasificar por ancho de viewport.
 * Espejo de tailwind.config.js / styles/_variables.scss ($bp-*) para que el
 * deviceType de JS coincida con el switch de layout CSS (clases `lg:` de Tailwind).
 */
export const BREAKPOINTS = { md: 768, lg: 1024, xl: 1280 };

const platform = {
  /** True cuando corre dentro de un WebView nativo (iOS o Android). */
  get isNativeApp() {
    return window.onApp?.() || false;
  },

  /** True cuando corre en un navegador (no WebView nativo). */
  get isWeb() {
    return !platform.isNativeApp;
  },

  /** True cuando corre en el WebView de iOS. */
  get isIOS() {
    return window.onIOSApp?.() || false;
  },

  /** True cuando corre en el WebView de Android. */
  get isAndroid() {
    return window.onAndroidApp?.() || false;
  },

  /** True cuando corre en un navegador móvil (no WebView). */
  get isMobileBrowser() {
    if (platform.isNativeApp) return false;
    try {
      return /Mobi|Android/i.test(navigator.userAgent);
    } catch {
      return false;
    }
  },

  /** True en tablet: pantalla ≥768px con puntero táctil, fuera de WebView. */
  get isTabletBrowser() {
    if (platform.isNativeApp) return false;
    try {
      return window.matchMedia('(min-width: 768px) and (pointer: coarse)').matches;
    } catch {
      return false;
    }
  },

  /** True en desktop: puntero preciso (ratón), fuera de WebView. */
  get isDesktopBrowser() {
    if (platform.isNativeApp) return false;
    try {
      return window.matchMedia('(pointer: fine)').matches;
    } catch {
      return false;
    }
  },

  /** True si hay acceso a la cámara vía API web o bridge nativo. */
  get hasCamera() {
    return platform.isNativeApp
      ? true
      : !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function');
  },

  /**
   * True si la biometría está disponible.
   * En browser siempre false (Fase 1 — Fase 2: WebAuthn).
   */
  get hasBiometrics() {
    return platform.isNativeApp;
  },

  /**
   * True si push notifications están disponibles.
   * En browser: requiere Service Worker + PushManager (Fase 2).
   */
  get hasPushSupport() {
    if (platform.isNativeApp) return true;
    return 'serviceWorker' in navigator && 'PushManager' in window;
  },

  /** True cuando corre como PWA instalada (display-mode standalone). */
  get isStandalone() {
    try {
      return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
    } catch {
      return false;
    }
  },

  /** Ancho de viewport actual en px. */
  get viewportWidth() {
    return window.innerWidth || document.documentElement.clientWidth || 0;
  },

  /**
   * Tipo de dispositivo por ancho de viewport:
   * mobile <768 · tablet 768–1023 · desktop ≥1024 (alineado a `lg:` de Tailwind).
   * En WebView nativo siempre 'mobile'.
   */
  get deviceType() {
    if (platform.isNativeApp) return 'mobile';
    const width = platform.viewportWidth;
    if (width >= BREAKPOINTS.lg) return 'desktop';
    if (width >= BREAKPOINTS.md) return 'tablet';
    return 'mobile';
  },
};

export default platform;
